<template>
  <div class="search search-home" :class="{ 'search-sticky': sticky }">
    <div class="search__controls">
      <InputBox @click="toggle('destination')" class="search__input-box" icon="map"
        :label="selectedDestinationLabel" :value="selectedDestinationValue" :highlight="dropdowns.destination">
        <Icon v-if="selectedDestinations.length" slot="clear" class="search__input-box--clear" icon="cross"
          @click="clear(destinations, 'airports'); allDestinationsToggleSelected(false)" />
        <Destination v-if="dropdowns.destination" class="input-box__dropdown"
          v-model="destinations" :allSelected="allDestinationsSelected"
          @adjustVisibility="adjustDropdownVisibility"
          @input="toggle('destination')"
          @close="toggle('destination')"
          @allSelected="allDestinationsToggleSelected" />
      </InputBox>

      <InputBox @click="toggle('airport')" class="search__input-box" icon="v"
        :label="selectedAirportLabel" :value="selectedAirportValue" :highlight="dropdowns.airport">
        <Icon v-if="selectedAirports.length" slot="clear" class="search__input-box--clear" icon="cross"
          @click="clear(airports, 'destinations'); allAirportsToggleSelected(false)" />
        <Airport v-if="dropdowns.airport" class="input-box__dropdown"
          v-model="airports" :allSelected="allAirportsSelected"
          @adjustVisibility="adjustDropdownVisibility"
          @input="toggle('airport')"
          @close="toggle('airport')"
          @allSelected="allAirportsToggleSelected" />
      </InputBox>

      <DatesInputBox class="search__input-box" ref="datesInputBox"
        :label="selectedDateLabel"
        :start="selectedDateStartValue" :end="selectedDateEndValue"
        :highlight="dropdowns.dates ? dates.mode : ''"
        @click="startEditDates">

        <Calendar v-if="dropdowns.dates" ref="calendar"
          v-model="dates"
          :validDates="dates.validDates"
          :ranges="dates.ranges"
          :validRanges="dates.validRanges"
          :autoCloseParent="$refs.datesInputBox.$el"
          @adjustVisibility="adjustDropdownVisibility"
          @updateEditedDates="updateEditedDates"
          @close="endEditDates"
          @input="updateAvailableValues" />
      </DatesInputBox>

      <InputBox @click="toggle('duration')" class="search__input-box" icon="v"
        :label="selectedDurationLabel" :value="selectedDurationValue" :highlight="dropdowns.duration">
        <Duration v-if="dropdowns.duration" class="input-box__dropdown"
          v-model="durations"
          @adjustVisibility="adjustDropdownVisibility"
          @close="toggle('duration')" />
      </InputBox>

      <InputBox @click="toggle('people')" class="search__input-box" label="" icon="v"
        :value="selectedPeopleValue" :highlight="dropdowns.people">
        <People v-if="dropdowns.people"
          v-model="people"
          @adjustVisibility="adjustDropdownVisibility"
          @input="toggle('people')"
          @close="toggle('people')" />
      </InputBox>

      <button role="button" @click="search" class="search__submit">
        Szukaj
        <Icon icon="v" />
      </button>
    </div>

    <div class="search__advertisment">{{ advertisment }}</div>
  </div>
</template>

<script>
import moment from 'moment'
import Vue from 'vue'
import Component from 'vue-class-component'

import Airport from './fields/AirportDropdown'
import Destination from './fields/DestinationDropdown'
import Duration from './fields/DurationDropdown'
import People from './fields/PeopleDropdown'
import InputBox from './InputBox.vue'
import AdvertismentLogic from './logic/Advertisment'
import AirportLogic from './logic/Airport'
import DatesLogic from './logic/DatesDesktop'
import DestinationLogic from './logic/Destination'
import DurationLogic from './logic/Duration'
import PeopleLogic from './logic/People'

import DatesInputBox from '@/components/DatesInputBox'
import Icon from '@/components/Icon.vue'
import DropdownHandler from '@/components/search/DropdownHandler'
import Calendar from '@/components/search/fields/CalendarDropdown'
import StickySearchMixin from '@/components/search/StickySearchMixin'
import * as events from '@/events'
import { API, SearchConstraintsRequest } from '@/modules/api'

@Component({
  mixins: [
    StickySearchMixin,
    DropdownHandler,
    DestinationLogic,
    AirportLogic,
    DatesLogic,
    DurationLogic,
    PeopleLogic,
    AdvertismentLogic,
  ],
  components: {
    InputBox,
    DatesInputBox,
    Icon,
    Airport,
    Calendar,
    Destination,
    Duration,
    People,
  },
  props: {
    form: {
      type: HTMLFormElement,
      required: true,
    },
  },
})
export default class DesktopHome extends Vue {
  dropdowns = {
    destination: false,
    airport: false,
    dates: false,
    duration: false,
    people: false,
  }

  created () {
    new API()
      .getSearchConstraints(new SearchConstraintsRequest())
      .then(this.readSearchConstraintsResponse)
      .then(events.appBeforeInitialize.subscribe(this.setComponentAsSticky))
  }

  search () {
    this.saveValuesToForm()
    this.form.submitWFFM()
  }

  saveValuesToForm () {
    this.dx.destination.data = this.destinations
    this.dx.airport.data = this.airports
    this.dx.dates.data = this.dates.selected
    this.dx.duration.data = this.durations
    this.dx.people.data = this.people
  }

  updateAvailableValues () {
    this.saveValuesToForm()
    return new API()
      .getSearchConstraints(SearchConstraintsRequest.fromWFFMForm(this.form))
      .then(this.updateSearchConstraintsResponse)
  }

  /**
   * @argument {SearchConstraintsResponse} response
   */
  readSearchConstraintsResponse (response) {
    const inThreeWeeks = moment(response.minDate).add({ days: 21 })
    this.dates.selected = moment.range(response.minDate, moment.min(response.maxDate, inThreeWeeks))
    this.dates.validDates = moment.range(response.minDate, response.maxDate)
    this.markAvailabilityOfListEntries(this.airports, response.airports)
    this.markAvailabilityOfListEntries(this.destinations, response.destinations)
    this.airports = this.airports.sort((a1, a2) => a1.name.localeCompare(a2.name))
    this.destinations = this.destinations.sort((d1, d2) => d1.name.localeCompare(d2.name))
    this.people = { ...this.people, checkChildrenBirthday: true }
    this.markAvailabilityOfRadioElements(this.durations.ranges, response.validRanges)
  }

  /**
   * @argument {SearchConstraintsResponse} response
   */
  updateSearchConstraintsResponse (response) {
    this.dates.selected = moment.range(moment.max(this.dates.selected.start, response.minDate), moment.min(this.dates.selected.end, response.maxDate))
    this.dates.validDates = moment.range(response.minDate, response.maxDate)
    this.markAvailabilityOfListEntries(this.airports, response.airports)
    this.markAvailabilityOfListEntries(this.destinations, response.destinations)
    this.airports = this.airports.sort((a1, a2) => a1.name.localeCompare(a2.name))
    this.destinations = this.destinations.sort((d1, d2) => d1.name.localeCompare(d2.name))
    this.markAvailabilityOfRadioElements(this.durations.ranges, response.validRanges)
  }

  markAvailabilityOfListEntries (list, ids) {
    list.forEach(item => {
      item.enabled = ids.some(x => x === item.id)
      if (!item.enabled) item.selected = false
    })
  }

  markAvailabilityOfRadioElements (list, ids) {
    list.forEach(item => {
      item.enabled = ids.some(x => x === item.value)
    })
  }

  selectAllFromList (list) {
    list.forEach(item => {
      if (item.enabled) item.selected = true
    })
  }

  updateAndReselectList (listToReselect) {
    this.updateAvailableValues().then(() => {
      if (this.allAirportsSelected && listToReselect == 'airports') this.selectAllFromList(this.airports)
      if (this.allDestinationsSelected && listToReselect == 'destinations') this.selectAllFromList(this.destinations)
    })
  }

  /**
   * Business requirement:
   * If we click on 'X' icon in DestinationDropdown or AirportDropdown we need to do few thinks:
   * - clear actual clicked control data
   * - update selected items in opossite control form element *but* only if there is selected checkbox to select all items.
   * If checkbox "allSelected" is selected we need to update it every time we change something in destinations/airports
   *
   * @param {Array} listToDeselect
   * @param {Array} listToReselect
   */
  clear (listToDeselect, listToReselect) {
    listToDeselect.forEach(item => { item.selected = false })
    this.updateAndReselectList(listToReselect)
  }
}
</script>

<style lang="scss" scoped>
.search {
  position: absolute;
  bottom: 60px;
  left: 0;
  right: 0;
  max-width: 730px;
  margin: auto;
  padding: 15px 30px 14px;
  background-color: #005ca9;
  z-index: 100;

  @include media(desktop) {
    max-width: 1240px;
  }

  &__controls {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    grid-column-gap: 30px;
    grid-row-gap: 20px;
    align-items: flex-end;

    // calendar inputbox
    div:nth-child(3) {
      min-width: 210px;
    }

    @include media(desktop) {
      grid-template-columns: repeat(3, 220px) 130px 170px 106px;
      grid-column-gap: 20px;
      grid-template-rows: 1fr;
    }
  }

  &__input-box {
    font-family: 'Gotham Book';
    margin-bottom: 0;
    font-weight: 400;
    text-transform: none;
    position: relative;
    color: #3c3939;
  }

  &__input-box--clear {
    font-size: 11px;
    padding: 4px 8px;
    margin-left: -8px;

    &:hover {
      color: #A0D5FF;
    }
  }

  &__submit {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 110px;
    height: 36px;
    background-color: transparent;
    border: 2px solid #fff;
    text-transform: uppercase;
    font-weight: bold;
    font-size: 13px;
    line-height: 13px;
    font-family: 'Gotham';
    margin: 0;
    padding: 7px 7px 7px 10px;
    color: #fff;
    transition: .05s all ease-in-out;
    outline: none;

    &:hover {
      background-color: #009FEA;
      border-color: #009FEA;
    }

    @include media(desktop) {
      position: absolute;
      right: 30px;
      top: 50%;
      transform: translate(0, -50%);
    }

    .figure {
      font-size: 6px;
      transform: rotate(-90deg);
      display: inline-block;
      margin-left: 8px;
    }
  }

  &__advertisment {
    text-align: center;
    font-family: Georgia;
    font-size: 14px;
    font-style: italic;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 15px;
  }

  .destination-dropdown {
    width: 615px;
  }

  .calendar-dropdown {
    position: absolute;
    margin-top: 4px;
    right: 0;

    @include media(desktop) {
      right: auto;
    }
  }
}

.search-sticky {
  position: fixed;
  top: 60px;
  bottom: auto;

  @include media(desktop) {
    top: 70px;
  }
}
</style>

<style lang="scss">
@import './DesktopSearch.scss';
</style>


// WEBPACK FOOTER //
// src/components/search/DesktopHome.vue